import { Download, KeyRound, LogOut } from 'lucide-react';
import { useRef, useState } from 'react';

import Modal from './Modal.jsx';

export default function SettingsForm({ profile, onClose, onSave, onExport, onLogout, busy }) {
  const [form, setForm] = useState(() => ({ quickBreastmilkInitial: 20, quickFormulaInitial: 20, quickNursingInitial: 1, quickFeedStep: 5, quickNursingStep: 1, quickHoldIntervalMs: 500, ...profile }));
  const [error, setError] = useState('');
  const quickTouched = useRef(new Set());

  function update(field, value) {
    if (field.startsWith('quick')) quickTouched.current.add(field);
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function submit(event) {
    event.preventDefault();
    setError('');
    try {
      const payload = { ...form };
      for (const field of ['quickBreastmilkInitial', 'quickFormulaInitial', 'quickNursingInitial', 'quickFeedStep', 'quickNursingStep', 'quickHoldIntervalMs']) {
        if (!quickTouched.current.has(field)) delete payload[field];
      }
      await onSave(payload);
    } catch (cause) {
      setError(cause.message);
    }
  }

  return (
    <Modal
      title="宝宝资料与设置"
      description="资料只保存在当前 Key 对应的家庭空间"
      onClose={onClose}
      wide
      footer={(
        <>
          <span />
          <div className="modal-footer__actions">
            <button type="button" className="button button--secondary" onClick={onClose}>取消</button>
            <button type="submit" form="settings-form" className="button button--primary" disabled={busy}>{busy ? '保存中…' : '保存资料'}</button>
          </div>
        </>
      )}
    >
      <form id="settings-form" className="form-stack" onSubmit={submit}>
        <div className="form-row">
          <label className="field"><span>宝宝昵称</span><input value={form.babyName} onChange={(e) => update('babyName', e.target.value)} maxLength="30" required /></label>
          <label className="field"><span>出生日期</span><input type="date" value={form.birthDate ?? ''} onChange={(e) => update('birthDate', e.target.value || null)} /></label>
        </div>
        <div className="form-row">
          <label className="field"><span>性别（用于 HPV 日程）</span><select value={form.sex} onChange={(e) => update('sex', e.target.value)}><option value="unknown">未设置</option><option value="female">女宝宝</option><option value="male">男宝宝</option></select></label>
          <label className="field"><span>地区 / 省份</span><input value={form.province ?? ''} onChange={(e) => update('province', e.target.value)} placeholder="用于核对地方接种安排" maxLength="30" /></label>
        </div>
        <fieldset className="field-group">
          <legend>长按快速记录</legend>
          <div className="form-row">
            <label className="field"><span>母乳瓶喂初始量（ml）</span><input type="number" min="5" max="500" step="1" value={form.quickBreastmilkInitial} onChange={(e) => update('quickBreastmilkInitial', e.target.value)} /></label>
            <label className="field"><span>奶粉瓶喂初始量（ml）</span><input type="number" min="5" max="500" step="1" value={form.quickFormulaInitial} onChange={(e) => update('quickFormulaInitial', e.target.value)} /></label>
          </div>
          <div className="form-row">
            <label className="field"><span>亲喂初始时长（分钟）</span><input type="number" min="1" max="180" step="1" value={form.quickNursingInitial} onChange={(e) => update('quickNursingInitial', e.target.value)} /></label>
            <label className="field"><span>瓶喂每次增加（ml）</span><input type="number" min="1" max="100" step="1" value={form.quickFeedStep} onChange={(e) => update('quickFeedStep', e.target.value)} /></label>
          </div>
          <div className="form-row">
            <label className="field"><span>亲喂每次增加（分钟）</span><input type="number" min="1" max="60" step="1" value={form.quickNursingStep} onChange={(e) => update('quickNursingStep', e.target.value)} /></label>
            <span />
          </div>
          <label className="field"><span>叠加间隔（毫秒）</span><input type="number" min="100" max="5000" step="100" value={form.quickHoldIntervalMs} onChange={(e) => update('quickHoldIntervalMs', e.target.value)} /></label>
          <p className="medical-note">长按并悬停在选项上时，按照此间隔增加喂养量或亲喂时长。</p>
        </fieldset>
        <fieldset className="field-group">
          <legend>乙脑接种方案</legend>
          <div className="segmented"><button type="button" className={form.jeScheme === 'live' ? 'is-active' : ''} aria-pressed={form.jeScheme === 'live'} onClick={() => update('jeScheme', 'live')}>减毒活疫苗 · 2 剂</button><button type="button" className={form.jeScheme === 'inactivated' ? 'is-active' : ''} aria-pressed={form.jeScheme === 'inactivated'} onClick={() => update('jeScheme', 'inactivated')}>灭活疫苗 · 4 剂</button></div>
        </fieldset>
        <fieldset className="field-group">
          <legend>甲肝接种方案</legend>
          <div className="segmented"><button type="button" className={form.hepAScheme === 'live' ? 'is-active' : ''} aria-pressed={form.hepAScheme === 'live'} onClick={() => update('hepAScheme', 'live')}>减毒活疫苗 · 1 剂</button><button type="button" className={form.hepAScheme === 'inactivated' ? 'is-active' : ''} aria-pressed={form.hepAScheme === 'inactivated'} onClick={() => update('hepAScheme', 'inactivated')}>灭活疫苗 · 2 剂</button></div>
        </fieldset>
        <p className="medical-note">接种方案请按接种证或门诊建议选择；本工作台只做记录和日期推算，不提供医疗诊断。</p>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
      </form>
      <div className="settings-actions">
        <button type="button" onClick={onExport}><Download size={18} /><span><strong>导出 JSON</strong><small>包含资料、原始记录与疫苗档案</small></span></button>
        <button type="button" onClick={onLogout}><LogOut size={18} /><span><strong>退出当前 Key</strong><small>不会删除家庭空间的数据</small></span></button>
      </div>
      <div className="key-notice"><KeyRound size={17} /><span>忘记 Key 无法找回原空间，请妥善保存。</span></div>
    </Modal>
  );
}
